import type { Route } from '@tuyau/core/types';
import {
  computed,
  effect,
  inject,
  Injectable,
  type Signal,
  signal,
  type WritableSignal,
} from '@angular/core';
import { AccountService } from '@core/auth';
import { TuyauService } from '@core/tuyau';

export type Notification =
  Route.Response<'employee_v2.notification.index'>['data'][number];

export type NotificationTab = 'all' | 'unread' | 'financeInvoice';

export type NotificationTabState = {
  notifications: Notification[];
  total: number;
  currentPage: number;
  lastPage: number;
  loading: boolean;
  loadingMore: boolean;
};

const PER_PAGE = 10;

const emptyState: NotificationTabState = {
  notifications: [],
  total: 0,
  currentPage: 0,
  lastPage: 0,
  loading: false,
  loadingMore: false,
};

const TAB_QUERY: Record<
  NotificationTab,
  { status: 'all' | 'unread'; type?: 'finance-invoice' }
> = {
  all: { status: 'all' },
  unread: { status: 'unread' },
  financeInvoice: { status: 'all', type: 'finance-invoice' },
};

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private tuyau = inject(TuyauService);
  private accountService = inject(AccountService);

  private readonly _states: Record<
    NotificationTab,
    WritableSignal<NotificationTabState>
  > = {
    all: signal(emptyState),
    unread: signal(emptyState),
    financeInvoice: signal(emptyState),
  };

  readonly states: Readonly<
    Record<NotificationTab, Signal<NotificationTabState>>
  > = this._states;

  readonly unreadCount = computed(() => this._states.unread().total);

  constructor() {
    // Wait for authentication before fetching so the request includes a valid token.
    effect(() => {
      if (this.accountService.profile()) {
        this.reloadUnread();
      }
    });
  }

  async reload() {
    await Promise.all(
      (Object.keys(this._states) as NotificationTab[]).map((tab) =>
        this.fetchPage(tab, 1, { replace: true }),
      ),
    );
  }

  async reloadUnread() {
    await this.fetchPage('unread', 1, { replace: true });
  }

  async loadMore(tab: NotificationTab) {
    const state = this._states[tab]();
    if (state.loadingMore) return;
    if (state.currentPage >= state.lastPage) return;
    await this.fetchPage(tab, state.currentPage + 1, { replace: false });
  }

  async markAsRead(ids: string[]) {
    if (!ids.length) return;
    await this.tuyau.client.api.employeeV2.notification.update({
      body: { read: true, ids },
    });
    this.reload();
  }

  async markAllAsRead() {
    const ids = await this.fetchAllIds('unread');
    await this.markAsRead(ids);
  }

  async delete(ids: string[]) {
    if (!ids.length) return;
    await this.tuyau.client.api.employeeV2.notification.destroy({
      body: { ids },
    });
    this.reload();
  }

  async deleteAll() {
    const ids = await this.fetchAllIds('all');
    await this.delete(ids);
  }

  /**
   * Walks every page of the given tab and returns all notification ids.
   * Used by mark-all / delete-all so they operate on the full server-side
   * set, not just what's currently loaded in the popover.
   */
  private async fetchAllIds(tab: NotificationTab): Promise<string[]> {
    const ids: string[] = [];
    const perPage = 100;
    let page = 1;

    // Cap at 100 pages (10 000 ids) to avoid runaway loops.
    for (let i = 0; i < 100; i++) {
      const response =
        await this.tuyau.client.api.employeeV2.notification.index({
          query: { ...TAB_QUERY[tab], page, perPage },
        });

      ids.push(...response.data.map((n) => n.id));

      const lastPage = Number(response.metadata.lastPage);
      if (page >= lastPage || response.data.length === 0) break;
      page++;
    }

    return ids;
  }

  private async fetchPage(
    tab: NotificationTab,
    page: number,
    { replace }: { replace: boolean },
  ) {
    const state = this._states[tab];

    state.update((s) => ({
      ...s,
      loading: replace,
      loadingMore: !replace,
    }));

    try {
      const response =
        await this.tuyau.client.api.employeeV2.notification.index({
          query: { ...TAB_QUERY[tab], page, perPage: PER_PAGE },
        });

      state.update((s) => ({
        notifications: replace
          ? response.data
          : [...s.notifications, ...response.data],
        total: Number(response.metadata.total),
        currentPage: Number(response.metadata.currentPage),
        lastPage: Number(response.metadata.lastPage),
        loading: false,
        loadingMore: false,
      }));
    } catch {
      state.update((s) => ({ ...s, loading: false, loadingMore: false }));
    }
  }
}
